import { FlatTreeControl } from '@angular/cdk/tree'
import { Component, inject, Input, OnInit } from '@angular/core'
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree'
import { Router } from '@angular/router'
import { UserAccount } from '../../../../api/models/user-account.model'
import { HierarchyMenuItem } from '../../model/hierarchy-menu.model'
import { AuthService } from '../../services/auth.service'
import { notUndefined } from '../../utils/conditional-assert'

interface HierarchyNode extends HierarchyMenuItem {
    expandable: boolean
}

export enum NavigationPaths {
    login,
    start,
    notfound,
    employees,
    timecards,
    timesheets,
    clients,
    accounts,
    notauthorized
}

@Component({
    selector: 'ppw-hierarch-menu',
    templateUrl: './hierarchy-menu.component.html',
    styleUrls: ['./hierarchy-menu.component.scss']
})
export class HierarchyMenuComponent implements OnInit {
    @Input() menuItems!: Array<HierarchyMenuItem>

    treeControl!: FlatTreeControl<HierarchyNode>
    treeFlattener!: MatTreeFlattener<HierarchyMenuItem, HierarchyNode>
    dataSource!: MatTreeFlatDataSource<HierarchyMenuItem, HierarchyNode>
    isLoaded = false
    isSelectedNode?: HierarchyNode
    authService: AuthService = inject(AuthService)
    private myAccount?: UserAccount
    constructor(private readonly router: Router) {}

    hasChild = (_: number, node: HierarchyNode) => node.expandable

    async ngOnInit(): Promise<void> {
        this.myAccount = await this.authService.fetchAccount()
        this.menuItems = this.filterNotAuthorisedMenuItems(this.menuItems)
        this.treeControl = new FlatTreeControl<HierarchyNode>(
            (node) => notUndefined(node.level),
            (node) => node.expandable
        )
        this.treeFlattener = new MatTreeFlattener(
            this.transformer,
            (node) => notUndefined(node.level),
            (node) => node.expandable,
            (node) => node.children
        )
        this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener)
        this.dataSource.data = this.menuItems
        const foundNode = this.treeControl.dataNodes.find((node) =>
            node.path ? this.router.url.endsWith(node.path) : false
        )
        if (foundNode) {
            this.expandParents(foundNode)
            this.isSelectedNode = foundNode
        }
        this.isLoaded = true
    }

    navigateTo(node: HierarchyNode): void {
        this.isSelectedNode = node
        void this.router.navigate([node.path])
    }

    private filterNotAuthorisedMenuItems(menuItems: Array<HierarchyMenuItem>): Array<HierarchyMenuItem> {
        const toReturn: Array<HierarchyMenuItem> = []
        menuItems.forEach((menuItem) => {
            if (menuItem.children) {
                menuItem.children = this.filterNotAuthorisedMenuItems(menuItem.children)
            }

            if (this.myAccount && this.authService.isAuthorizedForWithAccount(this.myAccount, menuItem.accessFor)) {
                toReturn.push(menuItem)
            }
        })

        return toReturn
    }

    private readonly transformer = (node: HierarchyMenuItem, level: number): HierarchyNode =>
        ({
            ...node,
            title: node.title,
            expandable: !!node.children && node.children.length > 0,
            level
        } as HierarchyNode)

    private expandParents(node: HierarchyNode): void {
        const parent = this.getParent(node)
        if (parent) {
            this.treeControl.expand(parent)
        }

        if (parent && this.treeControl.getLevel(parent) > 0) {
            this.expandParents(parent)
        }
    }

    private getParent(node: HierarchyNode): HierarchyNode | undefined {
        const currentLevel = this.treeControl.getLevel(node)

        if (currentLevel < 1) {
            return undefined
        }

        const startIndex = this.treeControl.dataNodes.indexOf(node) - 1

        for (let i = startIndex; i >= 0; i--) {
            const currentNode = this.treeControl.dataNodes[i]
            if (this.treeControl.getLevel(currentNode) < currentLevel) {
                return currentNode
            }
        }

        return undefined
    }
}
